import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Folder, FileText, RotateCcw, Trash2, Trash } from 'lucide-react';
import Spinner from '../../components/ui/Spinner';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useState } from 'react';
import { formatBytes, formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { useTenant } from '../../hooks/useTenant';
import { ACTIONS } from '../tenancy/actions';
import {
  deleteFileForever,
  deleteFolderForever,
  listTrashFiles,
  listTrashFolders,
  restoreFile,
  restoreFolder,
} from '../files/filesApi';

export default function TrashPage() {
  const { allows } = useTenant();
  const canManageFolders = allows(ACTIONS.FOLDER_MANAGE);
  const canWriteFiles = allows(ACTIONS.FILE_WRITE);
  const canDeleteFiles = allows(ACTIONS.FILE_DELETE);
  const queryClient = useQueryClient();
  const [purgeTarget, setPurgeTarget] = useState(null);

  const [foldersQ, filesQ] = useQueries({
    queries: [
      { queryKey: ['trash-folders'], queryFn: listTrashFolders },
      { queryKey: ['trash-files', {}], queryFn: () => listTrashFiles({ limit: 100 }) },
    ],
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['trash-folders'] });
    queryClient.invalidateQueries({ queryKey: ['trash-files'] });
    queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
    queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const restoreMutation = useMutation({
    mutationFn: ({ type, id }) => (type === 'folder' ? restoreFolder(id) : restoreFile(id)),
    onSuccess: () => {
      invalidate();
      setPurgeTarget(null);
      toast.success('Restored');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Restore failed')),
  });

  const purgeMutation = useMutation({
    mutationFn: ({ type, id }) =>
      type === 'folder' ? deleteFolderForever(id) : deleteFileForever(id),
    onSuccess: () => {
      invalidate();
      setPurgeTarget(null);
      toast.success('Permanently deleted');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Delete failed')),
  });

  const isLoading = foldersQ.isLoading || filesQ.isLoading;
  const folders = foldersQ.data ?? [];
  const files = filesQ.data?.data ?? [];
  const isEmpty = !isLoading && folders.length === 0 && files.length === 0;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center gap-2">
        <Trash className="h-5 w-5 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-800">Trash</h1>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
        {isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : isEmpty ? (
          <div className="py-16 text-center text-sm text-slate-400">Trash is empty.</div>
        ) : (
          [
            ...folders.map((f) => ({ ...f, _type: 'folder' })),
            ...files.map((f) => ({ ...f, _type: 'file' })),
          ].map((item) => (
            <div
              key={`${item._type}-${item.id}`}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5"
            >
              {item._type === 'folder' ? (
                <Folder className="h-5 w-5 text-amber-500" />
              ) : (
                <FileText className="h-5 w-5 text-slate-400" />
              )}
              <span className="flex-1 truncate text-sm font-medium text-slate-700">
                {item.name}
              </span>
              <span className="hidden w-24 text-right text-xs text-slate-500 sm:block">
                {item._type === 'file' ? formatBytes(item.sizeBytes) : '—'}
              </span>
              <span className="hidden w-40 text-right text-xs text-slate-500 md:block">
                {formatDate(item.deletedAt)}
              </span>
              {(item._type === 'folder' ? canManageFolders : canWriteFiles || canDeleteFiles) && <div className="flex items-center gap-1">
                {(item._type === 'folder' ? canManageFolders : canWriteFiles) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => restoreMutation.mutate({ type: item._type, id: item.id })}
                >
                  <RotateCcw className="h-4 w-4" />
                  Restore
                </Button>
                )}
                {(item._type === 'folder' ? canManageFolders : canDeleteFiles) && <Button variant="ghost" size="sm" onClick={() => setPurgeTarget({ type: item._type, item })}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>}
              </div>}
            </div>
          ))
        )}
      </div>
      <ConfirmDialog open={Boolean(purgeTarget)} onClose={() => setPurgeTarget(null)} onConfirm={() => purgeMutation.mutate({ type: purgeTarget.type, id: purgeTarget.item.id })} loading={purgeMutation.isPending} title="Permanently delete item?" description={purgeTarget ? `“${purgeTarget.item.name}” cannot be recovered after deletion.` : ''} confirmLabel="Delete permanently" />
    </div>
  );
}
